/**
 * @fileoverview Input/output schemas for the refine-synthesizer agent.
 *
 * This agent synthesizes findings, weaknesses, and strengths from a refine session
 * into a coherent editorial brief for the draft-refiner agent.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  IssueSeverityEnum,
  IssueConfidenceEnum,
  IssueDomainEnum,
} from '@/ai/agents/issue-classification';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Schema for a finding from critique agents.
 * Uses the unified issue classification schema.
 */
const FindingSchema = z.object({
  id: z.string().describe('Unique identifier for the finding.'),
  severity: IssueSeverityEnum.describe('Severity level: must_fix, should_fix, or could_fix'),
  confidence: IssueConfidenceEnum.describe('Confidence level: certain, likely, or possible'),
  category: z.string().describe('Category of the finding from unified taxonomy.'),
  description: z.string().describe('Description of the issue.'),
  excerpt: z.string().describe('The text excerpt where the issue was found.'),
  suggestion: z.string().optional().describe('Suggested fix for the issue.'),
});

/**
 * Schema for a weakness from critique agents.
 * Uses the unified issue classification schema.
 */
const WeaknessSchema = z.object({
  id: z.string().describe('Unique identifier for the weakness.'),
  severity: IssueSeverityEnum.describe('Severity level: must_fix, should_fix, or could_fix'),
  confidence: IssueConfidenceEnum.describe('Confidence level: certain, likely, or possible'),
  domain: IssueDomainEnum.describe('The domain of this weakness'),
  description: z.string().describe('Description of the weakness.'),
});

/**
 * Schema for a strength from critique agents.
 */
const StrengthSchema = z.object({
  id: z.string().describe('Unique identifier for the strength.'),
  text: z.string().describe('Description of the strength.'),
});

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.pov.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema)
  .extend({
    findings: z.array(FindingSchema).describe(
      'Array of findings selected for this refine session.'
    ),
    weaknesses: z.array(WeaknessSchema).describe(
      'Array of weaknesses selected for this refine session.'
    ),
    strengths: z.array(StrengthSchema).describe(
      'Array of all identified strengths to preserve.'
    ),
    additionalNotes: z.string().optional().describe(
      'Optional additional instructions from the user.'
    ),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema for the refine synthesizer.
 */
export const OutputSchema = z.object({
  editorialBrief: z.string().describe(
    'A complete, flowing editorial brief written directly to the draft-refiner agent. ' +
    'This text will be passed as the userMessage to the draft-refiner-v2 agent. ' +
    'It should clearly articulate which issues to address, how to approach them, ' +
    'and which strengths must be preserved.'
  ),
  prioritizedIssues: z.array(z.string()).describe(
    'The issues to address, listed in priority order (most important first).'
  ),
  strengthsToPreserve: z.array(z.string()).describe(
    'Key strengths that must NOT be changed or diminished during refinement.'
  ),
  synthesisRationale: z.string().describe(
    'Brief explanation of the synthesis approach: how issues were prioritized, ' +
    'any thematic connections identified, and the recommended refinement strategy.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
